import React, { useRef } from "react";
import { TextB, TextItalic, TextUnderline, ListBullets, ListNumbers, LinkSimple, TextStrikethrough } from "@phosphor-icons/react";

/**
 * Lightweight dependency-free rich text editor (contentEditable + execCommand).
 * Uncontrolled: writes initial HTML once, reports changes via onChange(html).
 * React 19 safe (no findDOMNode / no innerHTML re-sync that resets the caret).
 */
export default function RichTextEditor({ value = "", onChange, placeholder = "Write here…", testId }) {
  const ref = useRef(null);
  const initialized = useRef(false);

  if (ref.current && !initialized.current) {
    ref.current.innerHTML = value || "";
    initialized.current = true;
  }

  const emit = () => onChange?.(ref.current?.innerHTML || "");

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Enter URL (https://…)");
    if (url) exec("createLink", /^https?:\/\//i.test(url) ? url : `https://${url}`);
  };

  const Btn = ({ onClick, title, children }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title}
      className="w-8 h-8 flex items-center justify-center rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900">
      {children}
    </button>
  );

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:border-blue-500">
      <div className="flex items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-1.5 py-1">
        <Btn onClick={() => exec("bold")} title="Bold"><TextB size={16} weight="bold" /></Btn>
        <Btn onClick={() => exec("italic")} title="Italic"><TextItalic size={16} /></Btn>
        <Btn onClick={() => exec("underline")} title="Underline"><TextUnderline size={16} /></Btn>
        <Btn onClick={() => exec("strikeThrough")} title="Strikethrough"><TextStrikethrough size={16} /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn onClick={() => exec("insertUnorderedList")} title="Bulleted list"><ListBullets size={16} /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Numbered list"><ListNumbers size={16} /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn onClick={addLink} title="Insert link"><LinkSimple size={16} /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder}
        data-testid={testId}
        className="rte-content min-h-[160px] max-h-[420px] overflow-auto px-3 py-2.5 text-sm text-slate-800 leading-relaxed outline-none"
      />
    </div>
  );
}
